import { useState } from "react";
import ChartTooltip from "./ChartTooltip.jsx";
import { MUSCLE_BUCKETS } from "../utils/analytics.js";
import { deltaSign, formatNumber } from "../utils/format.js";
import { accentVars } from "../utils/palette.js";
import styles from "./ComparisonMuscles.module.css";

const SIZE = 200;
const C = SIZE / 2;
const MAX_R = 64;
const LABEL_R = MAX_R + 18;
const TICKS = [0.33, 0.66, 1];
const AXES = MUSCLE_BUCKETS.map((b, i) => ({ ...b, angle: i * 60 }));

function polar(angleDeg, r) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: C + r * Math.cos(rad), y: C + r * Math.sin(rad) };
}

function hexPoints(r) {
  return AXES.map(({ angle }) => {
    const { x, y } = polar(angle, r);
    return `${x},${y}`;
  }).join(" ");
}

function polygon(values, maxVal) {
  return AXES.map(({ angle, id }) => {
    const r = ((values[id] || 0) / maxVal) * MAX_R;
    const { x, y } = polar(angle, r);
    return `${x},${y}`;
  }).join(" ");
}

function DeltaChip({ delta, hasPrev }) {
  if (!hasPrev) return null;
  const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  return (
    <span className={`${styles.delta} ${styles[dir]}`}>
      {deltaSign(dir)}{delta !== 0 ? ` ${delta > 0 ? "+" : ""}${delta}` : ""}
    </span>
  );
}

function setCounts(buckets) {
  return Object.fromEntries((buckets || []).map((b) => [b.id, b.setCount]));
}

// "@a +3 sets": who trained this muscle most, and by how much over the next
// profile. With one clear leader it reads the same for two profiles as for four.
function leadNote(cells) {
  const sorted = [...cells].sort((x, y) => y.sets - x.sets);
  if (sorted.length < 2 || sorted[0].sets === sorted[1].sets) return "Even";
  return `@${sorted[0].username} +${sorted[0].sets - sorted[1].sets} sets`;
}

export default function ComparisonMuscles({ profiles, periodDays }) {
  const [tip, setTip] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const [hoveredId, setHoveredId] = useState(null);

  if (!profiles?.length) return null;

  const series = profiles.map((profile) => {
    const sets = setCounts(profile.analytics.muscleBuckets);
    const prev = setCounts(profile.analytics.previous?.muscleBuckets);
    return {
      username: profile.username,
      accent: profile.accent,
      sets,
      prev,
      total: Object.values(sets).reduce((s, v) => s + v, 0) || 1,
      prevTotal: Object.values(prev).reduce((s, v) => s + v, 0)
    };
  });

  const radarMax = Math.max(...AXES.flatMap(({ id }) => series.map((s) => s.sets[id] || 0)), 1);

  const cellsFor = (id) => series.map((s) => ({
    username: s.username,
    accent: s.accent,
    sets: s.sets[id] || 0,
    pct: ((s.sets[id] || 0) / s.total) * 100,
    delta: (s.sets[id] || 0) - (s.prev[id] || 0),
    hasPrev: s.prevTotal > 0
  }));

  const rows = AXES.map(({ id, label }) => ({ id, label, cells: cellsFor(id) }));
  const maxPct = Math.max(...rows.flatMap((r) => r.cells.map((c) => c.pct)), 1);

  const showTip = (id, e) => {
    const cells = cellsFor(id);
    setHoveredId(id);
    setTip({
      label: MUSCLE_BUCKETS.find((b) => b.id === id)?.label || id,
      cells,
      note: leadNote(cells)
    });
    setTipPos({ x: e.clientX, y: e.clientY });
  };
  const moveTip = (e) => setTipPos({ x: e.clientX, y: e.clientY });
  const hideTip = () => { setHoveredId(null); setTip(null); };

  // Drawn back to front so the first profile's shape stays readable on top.
  const layers = [...series].reverse();

  return (
    <div className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.label}>Muscle focus</span>
        <span className={styles.note}>% · sets · Δ vs prev</span>
      </div>
      <div className={styles.legend}>
        {series.map((s) => (
          <span key={s.username} className={styles.legendItem} style={accentVars(s.accent)}>@{s.username}</span>
        ))}
      </div>

      <div className={styles.radarWrap}>
        <svg key={periodDays} viewBox={`0 0 ${SIZE} ${SIZE}`} className={styles.radar}>
          {TICKS.map((t) => (
            <polygon key={t} points={hexPoints(MAX_R * t)} fill="none" stroke="var(--border-md)" strokeWidth="1" style={{ opacity: hoveredId ? 0.25 : 1 }} />
          ))}
          {AXES.map(({ angle, id }) => {
            const o = polar(angle, MAX_R);
            const on = hoveredId === id;
            return <line key={id} x1={C} y1={C} x2={o.x} y2={o.y} stroke={on ? "var(--soft)" : "var(--border)"} strokeWidth={on ? 1.5 : 1} />;
          })}
          {layers.map((s, i) => (
            <polygon
              key={s.username}
              points={polygon(s.sets, radarMax)}
              fill={s.accent.tint}
              stroke={s.accent.color}
              strokeWidth="1.5"
              strokeLinejoin="round"
              className={styles.poly}
              style={{ "--i": layers.length - 1 - i, opacity: hoveredId ? 0.55 : 1 }}
            />
          ))}
          {AXES.map(({ angle, id }) => {
            const on = hoveredId === id;
            return (
              <g key={`pt-${id}`} style={{ opacity: !hoveredId || on ? 1 : 0.3 }}>
                {layers.map((s) => {
                  const p = polar(angle, ((s.sets[id] || 0) / radarMax) * MAX_R);
                  return <circle key={s.username} cx={p.x} cy={p.y} r={on ? 4 : 2.5} fill={s.accent.color} />;
                })}
              </g>
            );
          })}
          {AXES.map(({ angle, label, id }) => {
            const { x, y } = polar(angle, LABEL_R);
            return (
              <text key={id} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                className={`${styles.axisLabel} ${hoveredId === id ? styles.axisLabelOn : ""}`}>
                {label}
              </text>
            );
          })}
          {AXES.map(({ angle, id }) => {
            const a = polar(angle - 30, MAX_R + 26);
            const b = polar(angle + 30, MAX_R + 26);
            return (
              <polygon key={`hit-${id}`} points={`${C},${C} ${a.x},${a.y} ${b.x},${b.y}`} fill="transparent"
                onMouseEnter={(e) => showTip(id, e)} onMouseMove={moveTip} onMouseLeave={hideTip} />
            );
          })}
        </svg>
      </div>

      <div className={styles.list}>
        {rows.map(({ id, label, cells }) => (
          <div key={id} className={`${styles.row} ${hoveredId === id ? styles.rowOn : ""}`}
            onMouseEnter={(e) => showTip(id, e)} onMouseMove={moveTip} onMouseLeave={hideTip}>
            <span className={styles.muscle}>{label}</span>
            <div className={styles.sides}>
              {cells.map((cell) => (
                <div key={cell.username} className={styles.side} style={accentVars(cell.accent)}>
                  <div className={styles.track}>
                    <div className={styles.fill} style={{ width: `${(cell.pct / maxPct) * 100}%` }} />
                  </div>
                  <span className={styles.pct}>{Math.round(cell.pct)}%</span>
                  <span className={styles.sets}>{formatNumber(cell.sets)}</span>
                  <DeltaChip delta={cell.delta} hasPrev={cell.hasPrev} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {tip && (
        <ChartTooltip className={styles.tooltip} x={tipPos.x} y={tipPos.y}>
          <span className={styles.tipLabel}>{tip.label}</span>
          {tip.cells.map((cell) => (
            <span key={cell.username} className={styles.tipRow} style={accentVars(cell.accent)}>
              @{cell.username}: {formatNumber(cell.sets)} sets · {Math.round(cell.pct)}%
            </span>
          ))}
          <span className={styles.tipDiff}>{tip.note}</span>
        </ChartTooltip>
      )}
    </div>
  );
}
